// +----------------------------------------------------------------------
// | 偏好设置：AI Memory 开关 / 快速模式追问上限 / AI 语气 / 账号安全
// |
// | 账号安全（2026-09-28）加了两件事：
// |   ① 修改密码 —— 要验旧密码，改完踢掉其他设备（本机不踢）
// |   ② 绑定邮箱 —— 忘了密码时的唯一自助通道，存量老用户在这里补
// +----------------------------------------------------------------------
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getSettings,
  updateSettings,
  getProfile,
  changePassword,
  bindEmail,
} from '../../api/user';
import { toast } from '../../components/Toast';
import useAuth from '../../hooks/useAuth';
import './Settings.less';

const TONE_OPTIONS = [
  { value: 'friendly', label: '温和一点', desc: '说得客气些，多给点鼓励' },
  { value: 'direct', label: '直说就好', desc: '别绕弯子，直接讲重点' },
];

const QUESTION_OPTIONS = [1, 2, 3, 4, 5];

/** 跟注册页同一套粗校验：只拦明显不像邮箱的，不跟后端规则较劲 */
const looksLikeEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);

export default function Settings() {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    memory_enabled: 1,
    quick_max_questions: 3,
    ai_tone: 'friendly',
  });

  // 账号安全
  const [email, setEmail] = useState('');
  const [modal, setModal] = useState('');        // '' | 'password' | 'email'
  const [modalError, setModalError] = useState('');
  const [modalBusy, setModalBusy] = useState(false);
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [newPwd2, setNewPwd2] = useState('');
  const [emailInput, setEmailInput] = useState('');

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const data = await getSettings();
        if (!mounted) return;
        setSettings({
          memory_enabled: data.memory_enabled === undefined ? 1 : Number(data.memory_enabled),
          quick_max_questions: Number(data.quick_max_questions || 3),
          ai_tone: data.ai_tone || 'friendly',
        });
      } catch (e) {
        if (mounted) toast(e.message || '设置没拉到', 'error');
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    // 邮箱单独拉一次：/user/settings 只装偏好项，账号字段在 profile 里
    (async () => {
      try {
        const data = await getProfile();
        if (mounted && data && data.user) setEmail(data.user.email || '');
      } catch (e) {
        // 拉不到就当没绑，不打断设置页其它功能
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  /** 改动即保存（设置项少，不做「保存」按钮更轻） */
  const save = async (partial) => {
    const next = { ...settings, ...partial };
    setSettings(next);
    setSaving(true);
    try {
      await updateSettings(partial);
      toast('已保存', 'success');
    } catch (e) {
      setSettings(settings); // 失败回滚
      toast(e.message || '没保存上', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // ------------------------------ 账号安全弹层 ------------------------------

  const openModal = (kind) => {
    setModal(kind);
    setModalError('');
    setOldPwd('');
    setNewPwd('');
    setNewPwd2('');
    setEmailInput(kind === 'email' ? email : '');
  };

  const closeModal = () => {
    setModal('');
    setModalError('');
  };

  const submitPassword = async () => {
    if (modalBusy) return;

    if (!oldPwd) {
      setModalError('请填写当前密码');
      return;
    }
    if (newPwd.length < 6) {
      setModalError('新密码至少 6 位');
      return;
    }
    if (newPwd !== newPwd2) {
      setModalError('两次输入的新密码不一致');
      return;
    }

    setModalError('');
    setModalBusy(true);
    try {
      const data = await changePassword(oldPwd, newPwd);
      closeModal();
      const kicked = data ? Number(data.kicked || 0) : 0;
      toast(kicked > 0 ? `密码已更新，${kicked} 台其他设备已退出` : '密码已更新', 'success');
    } catch (e) {
      setModalError(e.message || '没改成，稍后再试');
    } finally {
      setModalBusy(false);
    }
  };

  const submitEmail = async () => {
    if (modalBusy) return;

    const mail = emailInput.trim();
    if (mail !== '' && !looksLikeEmail(mail)) {
      setModalError('邮箱格式不对，检查一下');
      return;
    }

    setModalError('');
    setModalBusy(true);
    try {
      const data = await bindEmail(mail);
      setEmail((data && data.email) || '');
      closeModal();
      toast(mail === '' ? '已解绑邮箱' : '邮箱已绑定', 'success');
    } catch (e) {
      setModalError(e.message || '没绑上，稍后再试');
    } finally {
      setModalBusy(false);
    }
  };

  if (loading) {
    return <div className="page center muted">正在加载…</div>;
  }

  return (
    <div className="page settings">
      <h1 className="settings-title">偏好设置</h1>

      <div className="card">
        <div className="settings-row">
          <div className="settings-row-main">
            <strong>让 AI 记住我</strong>
            <span className="muted">
              记住你的偏好和习惯（比如「比较看重性价比」），下次分析时用得上。随时可以关掉，也可以删掉已记的内容。
            </span>
          </div>
          <button
            type="button"
            className={`switch ${Number(settings.memory_enabled) === 1 ? 'is-on' : ''}`}
            onClick={() =>
              save({ memory_enabled: Number(settings.memory_enabled) === 1 ? 0 : 1 })
            }
            disabled={saving}
            aria-label="记忆开关"
          >
            <span className="switch-knob" />
          </button>
        </div>
      </div>

      <div className="card">
        <div className="settings-block">
          <strong>⚡ 30 秒决定，最多问几个问题</strong>
          <span className="muted">问得越少越快，但可能不够准。</span>
          <div className="settings-options">
            {QUESTION_OPTIONS.map((num) => (
              <button
                key={num}
                type="button"
                className={`settings-option ${Number(settings.quick_max_questions) === num ? 'is-active' : ''}`}
                onClick={() => save({ quick_max_questions: num })}
                disabled={saving}
              >
                {num} 个
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="settings-block">
          <strong>AI 说话的语气</strong>
          <span className="muted">影响它给你的分析怎么表达，不影响判断。</span>
          <div className="settings-tone">
            {TONE_OPTIONS.map((item) => (
              <button
                key={item.value}
                type="button"
                className={`settings-tone-item ${settings.ai_tone === item.value ? 'is-active' : ''}`}
                onClick={() => save({ ai_tone: item.value })}
                disabled={saving}
              >
                <strong>{item.label}</strong>
                <span className="muted">{item.desc}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 账号安全：改密码 / 绑邮箱 */}
      <div className="card">
        <div className="settings-row">
          <div className="settings-row-main">
            <strong>修改密码</strong>
            <span className="muted">改完其他登录的设备会退出，这台不受影响。</span>
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => openModal('password')}>
            修改
          </button>
        </div>

        <div className="settings-divider" />

        <div className="settings-row">
          <div className="settings-row-main">
            <strong>邮箱</strong>
            <span className="muted">
              {email ? `已绑定 ${email}` : '还没绑定。绑了之后忘了密码能自己找回，不用找客服。'}
            </span>
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => openModal('email')}>
            {email ? '修改' : '去绑定'}
          </button>
        </div>
      </div>

      <div className="card settings-danger">
        <div className="settings-row">
          <div className="settings-row-main">
            <strong>退出登录</strong>
            <span className="muted">纠结的记录会留在账号里，下次登录还在。</span>
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={handleLogout}>
            退出
          </button>
        </div>
      </div>

      {/* ---------------- 修改密码弹层 ---------------- */}
      {modal === 'password' && (
        <div className="settings-modal" onClick={closeModal}>
          <div className="settings-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="settings-modal-title">修改密码</div>
            <div className="settings-modal-tip">
              为了确认是你本人，先输一下现在的密码。改完其他设备会退出登录。
            </div>

            {modalError && <div className="settings-modal-err">{modalError}</div>}

            <div className="field">
              <label htmlFor="oldPassword">当前密码</label>
              <input
                id="oldPassword"
                className="input"
                type="password"
                autoComplete="current-password"
                value={oldPwd}
                onChange={(e) => setOldPwd(e.target.value)}
              />
            </div>

            <div className="field">
              <label htmlFor="newPassword">新密码</label>
              <input
                id="newPassword"
                className="input"
                type="password"
                autoComplete="new-password"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                placeholder="至少 6 位"
              />
            </div>

            <div className="field">
              <label htmlFor="newPassword2">确认新密码</label>
              <input
                id="newPassword2"
                className="input"
                type="password"
                autoComplete="new-password"
                value={newPwd2}
                onChange={(e) => setNewPwd2(e.target.value)}
                placeholder="再输一次"
              />
            </div>

            <div className="settings-modal-actions">
              <button type="button" className="btn btn-ghost" onClick={closeModal} disabled={modalBusy}>
                取消
              </button>
              <button type="button" className="btn btn-primary" onClick={submitPassword} disabled={modalBusy}>
                {modalBusy ? '提交中…' : '确定'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- 绑定邮箱弹层 ---------------- */}
      {modal === 'email' && (
        <div className="settings-modal" onClick={closeModal}>
          <div className="settings-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="settings-modal-title">{email ? '修改邮箱' : '绑定邮箱'}</div>
            <div className="settings-modal-tip">
              用来收密码重置链接。只在找回密码时用，不会给你发广告。
            </div>

            {modalError && <div className="settings-modal-err">{modalError}</div>}

            <div className="field">
              <label htmlFor="bindEmail">邮箱</label>
              <input
                id="bindEmail"
                className="input"
                type="email"
                autoComplete="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="你的邮箱"
              />
            </div>

            {email && (
              <div className="settings-modal-tip" style={{ margin: '10px 0 0' }}>
                清空内容再保存就是解绑，解绑后忘了密码就只能找客服了。
              </div>
            )}

            <div className="settings-modal-actions">
              <button type="button" className="btn btn-ghost" onClick={closeModal} disabled={modalBusy}>
                取消
              </button>
              <button type="button" className="btn btn-primary" onClick={submitEmail} disabled={modalBusy}>
                {modalBusy ? '保存中…' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
